import {
  useModalContext,
  WSBadge,
  WSButton,
  WSButtons,
  WSFlexBox,
  WSModal,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PayoutDestinationType,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { DetailsLayout } from "../../../components/DetailsLayout/DetailsLayout";
import { InstitutionLogoCircle } from "../../../components/InstitutionLogo/InstitutionLogoCircle";
import { useOpenSessionOTPModal } from "../../../components/SessionOTPModal/SessionOTPModal";
import { getAccountName } from "../../../components/WSAccountsListV2/utils";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { QUERY_PAYOUT_SETTINGS } from "../../../query/payments/keys";
import { usePayoutSettings } from "../../../query/payments/queries";
import { QUERY_USERS_ACCOUNTS } from "../../../query/users/keys";
import { useAccount } from "../../../query/users/queries";
import { WSQueries } from "../../../query/WSQuery";
import { paymentsService } from "../../../services/payments";
import { usersService } from "../../../services/users";

const CONFIRM_REMOVE_MODAL = "CONFIRM_REMOVE_MODAL";

export const AccountDetails: React.FC<RouteComponentProps<{
  accountId: string;
}>> = ({ match }) => {
  const accountId = match.params.accountId;
  const userId = useUserId();
  const history = useHistory();
  const { openModal, closeModal } = useModalContext();
  const accountQuery = useAccount(accountId);
  const payoutSettingsQuery = usePayoutSettings(userId);
  const [openSessionOTPModal] = useOpenSessionOTPModal();
  const standardPayoutDestination = payoutSettingsQuery.data?.payoutDestinations.find(
    destination => destination.payoutMethod === PayoutPreferences.Standard
  );
  const instantPayoutDestination = payoutSettingsQuery.data?.payoutDestinations.find(
    destination => destination.payoutMethod === PayoutPreferences.Instant
  );

  const isDefaultPayoutAccount =
    standardPayoutDestination?.destinationId === accountId;
  const isInstantPayoutAccount =
    instantPayoutDestination?.destinationId === accountId;

  const isPayoutEligible = accountQuery.data?.canBeUsedFor?.payouts;
  const isInstantPayoutEligible = accountQuery.data?.canBeUsedFor?.rtp;

  const [setForPayouts, setForPayoutsMeta] = useWSMutation(
    async () => {
      try {
        await paymentsService.payoutSettings.update(userId, {
          standard: {
            destinationId: accountId,
            destinationType: PayoutDestinationType.Account
          }
        });
      } catch (error) {
        if ((error as any)?.response?.data.errorSubType === "OtpRequired") {
          openSessionOTPModal({
            onSuccessVerifyOtp: () => {
              // retry the api
              setForPayouts();
            }
          });
        }
        throw error;
      }
    },
    {
      dependencies: [QUERY_PAYOUT_SETTINGS],
      onSuccess: () => {
        history.push("/member/settings/payment-methods");
      }
    }
  );

  const [setForInstantPayouts, setForInstantPayoutsMeta] = useWSMutation(
    async () => {
      try {
        await paymentsService.payoutSettings.update(userId, {
          instant: {
            destinationId: accountId,
            destinationType: PayoutDestinationType.Account
          }
        });
      } catch (error) {
        if ((error as any)?.response?.data.errorSubType === "OtpRequired") {
          openSessionOTPModal({
            onSuccessVerifyOtp: () => {
              // retry the api
              setForInstantPayouts();
            }
          });
        }
        throw error;
      }
    },
    {
      dependencies: [QUERY_PAYOUT_SETTINGS],
      onSuccess: () => {
        history.push("/member/settings/payment-methods");
      }
    }
  );

  const [remove, removeMeta] = useWSMutation(
    async () => {
      try {
        await paymentsService.payoutSettings.update(userId, {
          ...(isDefaultPayoutAccount ? { standard: null } : {}),
          ...(isInstantPayoutAccount ? { instant: null } : {})
        });
        await usersService.account.delete(accountId);
      } catch (error) {
        if ((error as any)?.response?.data.errorSubType === "OtpRequired") {
          openSessionOTPModal({
            onSuccessVerifyOtp: () => {
              // retry the api
              remove();
            }
          });
        }
        throw error;
      }
    },
    {
      dependencies: [QUERY_USERS_ACCOUNTS, QUERY_PAYOUT_SETTINGS],
      onSuccess: () => {
        closeModal(CONFIRM_REMOVE_MODAL);
        history.push("/member/settings/payment-methods");
      }
    }
  );

  return (
    <DetailsLayout
      onBack={() => {
        history.push("/member/settings/payment-methods");
      }}
      title="Account info"
    >
      <WSModal name={CONFIRM_REMOVE_MODAL} size="XS" title="Are you sure?">
        <WSErrorMessage
          mb="M"
          contextKey="PayoutSettings"
          error={removeMeta.error}
        />
        <WSButtons forceFullWidth>
          <WSButton.Destructive onClick={remove} loading={removeMeta.isLoading}>
            Remove account
          </WSButton.Destructive>

          <WSButton.Secondary
            onClick={() => {
              closeModal(CONFIRM_REMOVE_MODAL);
            }}
          >
            Cancel
          </WSButton.Secondary>
        </WSButtons>
      </WSModal>
      <WSQueries queries={{ accountQuery, payoutSettingsQuery }}>
        {({ accountQuery: { data: account } }) => {
          return (
            <DetailsLayout.Card>
              <WSFlexBox.CenterY wrap="nowrap" justify="space-between" mb="M">
                <WSFlexBox.CenterY wrap="nowrap">
                  <InstitutionLogoCircle
                    mr="M"
                    institutionId={account.institutionId}
                  />
                  <WSText.Heading4>{getAccountName(account)}</WSText.Heading4>
                </WSFlexBox.CenterY>
              </WSFlexBox.CenterY>
              <WSFlexBox mb="M">
                {isDefaultPayoutAccount && (
                  <WSBadge
                    mr={isInstantPayoutAccount ? "S" : "NONE"}
                    backgroundColor="green200"
                    textProps={{ color: "green500", singleLine: true }}
                    text="Standard Payout Method"
                  />
                )}
                {isInstantPayoutAccount && (
                  <WSBadge
                    backgroundColor="blue200"
                    textProps={{ color: "blue400", singleLine: true }}
                    text="Instant Payout Method"
                  />
                )}
              </WSFlexBox>

              <WSErrorMessage
                mb="M"
                contextKey="PayoutSettings"
                error={removeMeta.error}
              />

              <WSButtons forceFullWidth>
                <WSButton.Destructive
                  onClick={() => {
                    openModal(CONFIRM_REMOVE_MODAL);
                  }}
                >
                  Remove account
                </WSButton.Destructive>
                {!isDefaultPayoutAccount && isPayoutEligible && (
                  <WSButton
                    onClick={setForPayouts}
                    loading={setForPayoutsMeta.isLoading}
                  >
                    Make this my standard payout method
                  </WSButton>
                )}
                {!isInstantPayoutAccount && isInstantPayoutEligible && (
                  <WSButton.Secondary
                    onClick={setForInstantPayouts}
                    loading={setForInstantPayoutsMeta.isLoading}
                  >
                    Make this my instant payout method
                  </WSButton.Secondary>
                )}
              </WSButtons>
            </DetailsLayout.Card>
          );
        }}
      </WSQueries>
    </DetailsLayout>
  );
};
